import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { ToastService } from '../toast/toast.service';

/**
 * Replaces the old HttpService.setHeaders()/getHeader/postHeader pattern.
 * Attaches the bearer token (read fresh from localStorage on every request,
 * so logging in/out mid-session no longer requires re-calling setHeaders())
 * and forces withCredentials: true on every outgoing request.
 *
 * Register in app.config.ts:
 *   provideHttpClient(withInterceptors([authInterceptor]))
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const token = localStorage.getItem('token');

  const cloned = req.clone({
    withCredentials: true,
    setHeaders: token
      ? { Authorization: `Bearer ${token}` }
      : {}
  });

  return next(cloned);
};

/**
 * Reacts to session expiry/invalid token globally, instead of relying on
 * manual getAuthentication() checks scattered through the app.
 *
 * Your backend's isAuthenticated middleware returns 401 whenever the JWT
 * is missing, malformed, or expired - so any 401 from any protected
 * endpoint means "the session is no longer valid."
 *
 * The /auth/login URL is excluded so a failed login on the login
 * form doesn't get treated as "session expired" and bounce the user
 * straight back to /Logout mid-login.
 *
 * A 403 means the session is valid but the backend's role/organisation
 * checks refused the request, so the user stays logged in and gets told.
 *
 * Register alongside authInterceptor in app.module.ts:
 *   provideHttpClient(withInterceptors([authInterceptor, authErrorInterceptor]))
 */
export const authErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const toast = inject(ToastService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !req.url.includes('/auth/login')) {
        authService.logout();
        router.navigateByUrl('/login');
        toast.show('Session timed out! Please login again.', 'warn');
      } else if (error.status === 403 && !req.url.includes('/auth/login')) {
        // On login, 403 means the organisation isn't set up; the login page says so itself
        toast.show("You don't have permission to do that.", 'error');
      }
      return throwError(() => error);
    })
  );
};
